import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { fetchAuthSession, signOut as amplifySignOut, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import type { Role } from '@synapse/shared-types';

// WHY: 30-minute inactivity timeout per security requirements (token management section).
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

// WHY: Amplify v6 stores OAuth scratch data and tokens under this prefix.
// We read/write the same keys so fetchAuthSession() and getCurrentUser()
// pick up tokens we wrote during a manual code-for-token exchange.
const AMPLIFY_KEY_PREFIX = 'CognitoIdentityServiceProvider';

interface AuthUser {
  sub: string;
  email: string;
  givenName?: string;
  familyName?: string;
  roles: Role[];
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

// ---------------------------------------------------------------------------
// JWT helpers (no external deps — minimal base64url decode for payload claims)
// ---------------------------------------------------------------------------

function decodeJwtPayload(jwt: string): Record<string, unknown> {
  const parts = jwt.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT — expected 3 segments');
  }
  const payload = parts[1];
  if (!payload) throw new Error('Invalid JWT — missing payload segment');
  // base64url → base64
  const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const decoded = atob(padded + padding);
  return JSON.parse(decoded) as Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Manual OAuth code → token exchange (bypasses Amplify's flaky internal flow)
// ---------------------------------------------------------------------------

interface CognitoTokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  error?: string;
  error_description?: string;
}

async function exchangeCodeForTokens(): Promise<void> {
  const clientId = import.meta.env['VITE_COGNITO_CLIENT_ID'] as string | undefined;
  const cognitoDomain = import.meta.env['VITE_COGNITO_DOMAIN'] as string | undefined;
  const cognitoRegion = (import.meta.env['VITE_COGNITO_REGION'] as string | undefined) ?? 'us-west-2';

  if (!clientId || !cognitoDomain) {
    throw new Error('Cognito client ID or domain not configured');
  }

  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');

  if (!code || !state) {
    throw new Error('OAuth callback missing code or state');
  }

  // WHY: Validate state matches what we stored when initiating the redirect.
  // This is the CSRF protection step — Amplify normally does this for us.
  const storedState = localStorage.getItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.oauthState`);
  if (storedState && storedState !== state) {
    throw new Error('OAuth state mismatch — possible CSRF attack or stale session');
  }

  // WHY: Read the PKCE code_verifier that signInWithRedirect stored before
  // navigating to the IdP. Without this, the token endpoint will reject us.
  const codeVerifier = localStorage.getItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.oauthPKCE`);
  if (!codeVerifier) {
    throw new Error('PKCE verifier not found in localStorage — cannot exchange code');
  }

  // WHY: redirect_uri MUST exactly match what was sent in the /authorize request
  // and must be one of the configured callback URLs on the User Pool client.
  // signInWithRedirect uses `${origin}/` (with trailing slash).
  const redirectUri = `${window.location.origin}/`;
  const tokenEndpoint = `https://${cognitoDomain}.auth.${cognitoRegion}.amazoncognito.com/oauth2/token`;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  console.log('[AuthProvider] Exchanging code at', tokenEndpoint);

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const data = (await response.json()) as CognitoTokenResponse;

  if (!response.ok || data.error) {
    const message = data.error_description ?? data.error ?? `HTTP ${response.status}`;
    throw new Error(`Token exchange failed: ${message}`);
  }

  if (!data.access_token || !data.id_token) {
    throw new Error('Token endpoint returned no access_token or id_token');
  }

  // WHY: Persist tokens to the exact localStorage keys Amplify v6 expects.
  // After this, fetchAuthSession() and getCurrentUser() return the tokens
  // as if Amplify had performed the exchange itself.
  // Key layout (from @aws-amplify/auth tokenProvider/TokenStore):
  //   CognitoIdentityServiceProvider.{clientId}.LastAuthUser
  //   CognitoIdentityServiceProvider.{clientId}.{username}.accessToken
  //   CognitoIdentityServiceProvider.{clientId}.{username}.idToken
  //   CognitoIdentityServiceProvider.{clientId}.{username}.refreshToken
  //   CognitoIdentityServiceProvider.{clientId}.{username}.clockDrift
  const accessPayload = decodeJwtPayload(data.access_token);
  const username = (accessPayload['username'] as string) ?? (accessPayload['sub'] as string) ?? 'username';

  const accessIatMs = ((accessPayload['iat'] as number | undefined) ?? 0) * 1000;
  const clockDrift = accessIatMs > 0 ? accessIatMs - Date.now() : 0;

  const userPrefix = `${AMPLIFY_KEY_PREFIX}.${clientId}.${username}`;
  localStorage.setItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.LastAuthUser`, username);
  localStorage.setItem(`${userPrefix}.accessToken`, data.access_token);
  localStorage.setItem(`${userPrefix}.idToken`, data.id_token);
  if (data.refresh_token) {
    localStorage.setItem(`${userPrefix}.refreshToken`, data.refresh_token);
  }
  localStorage.setItem(`${userPrefix}.clockDrift`, String(clockDrift));

  // WHY: Mirror Amplify's post-exchange cleanup so a subsequent SSO attempt
  // starts from a clean slate. These keys are checked by Amplify's own flow
  // and lingering values can cause "User cancelled OAuth flow." errors.
  localStorage.removeItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.inflightOAuth`);
  localStorage.removeItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.oauthPKCE`);
  localStorage.removeItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.oauthState`);
  // WHY: This signals to Amplify that the current session is OAuth-originated
  // (matches what completeOAuthFlow.completeFlow() writes via storeOAuthSignIn).
  localStorage.setItem(`${AMPLIFY_KEY_PREFIX}.${clientId}.oauthSignIn`, 'true,false');

  console.log('[AuthProvider] Manual token exchange succeeded for user', username);
}

async function fetchRoles(idToken: string): Promise<Role[]> {
  const apiUrl = import.meta.env['VITE_API_URL'] as string | undefined;
  if (!apiUrl) return [];

  const res = await fetch(`${apiUrl}/api/auth/me`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (!res.ok) return [];

  const body = (await res.json()) as { roles?: Role[] };
  return body.roles ?? [];
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSession = useCallback(async () => {
    try {
      await amplifySignOut();
    } catch {
      // Best-effort sign out
    }
    setUser(null);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    inactivityTimer.current = setTimeout(() => {
      void clearSession();
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearSession]);

  // WHY: One-shot check on mount. Two paths:
  //   1. Browser landed on /?code=...&state=... → run our manual exchange,
  //      strip the params, then load the session from the tokens we just wrote.
  //   2. No callback params → check existing session (cached tokens or refresh).
  useEffect(() => {
    let cancelled = false;

    async function loadSessionFromTokens(): Promise<AuthUser | null> {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      if (!idToken) return null;

      const payload = idToken.payload;
      const sub = (payload['sub'] as string) ?? '';
      const email = (payload['email'] as string) ?? '';
      const givenName = (payload['given_name'] as string) ?? undefined;
      const familyName = (payload['family_name'] as string) ?? undefined;
      // WHY: Send the ID token (not access token) — see useApiClient.ts and
      // apps/api/src/auth/jwt.ts for the full reasoning. Cognito custom:*
      // claims required for role mapping live only on ID tokens.
      const roles = await fetchRoles(idToken.toString());
      return { sub, email, givenName, familyName, roles };
    }

    async function checkSession() {
      const hasCode = window.location.search.includes('code=');
      const hasState = window.location.search.includes('state=');

      if (hasCode && hasState) {
        try {
          await exchangeCodeForTokens();
          // Clean up the URL so a refresh doesn't try to re-exchange the code.
          window.history.replaceState({}, '', window.location.pathname);

          const next = await loadSessionFromTokens();
          if (!cancelled) {
            setUser(next);
            setIsLoading(false);
          }
        } catch (err) {
          console.error('[AuthProvider] OAuth code exchange failed:', err);
          // Strip the params and bounce to login so the user can retry.
          window.history.replaceState({}, '', window.location.pathname);
          if (!cancelled) {
            setUser(null);
            setIsLoading(false);
          }
        }
        return;
      }

      try {
        await getCurrentUser();
        const next = await loadSessionFromTokens();
        if (!cancelled) {
          setUser(next);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setIsLoading(false);
        }
      }
    }

    void checkSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // WHY: Hub listener catches the password-flow `signedIn` event (signIn() call
  // from Login.tsx). The OAuth/SSO path is handled by the manual exchange above,
  // not via Amplify's signInWithRedirect Hub event.
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async (data) => {
      const event = data.payload.event;
      console.log('[AuthProvider] Hub auth event:', event);

      if (event === 'signedIn') {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          if (idToken) {
            const payload = idToken.payload;
            const sub = (payload['sub'] as string) ?? '';
            const email = (payload['email'] as string) ?? '';
            const givenName = (payload['given_name'] as string) ?? undefined;
            const familyName = (payload['family_name'] as string) ?? undefined;
            // WHY: Send the ID token to fetchRoles — see jwt.ts for why.
            const roles = await fetchRoles(idToken.toString());
            setUser({ sub, email, givenName, familyName, roles });
            setIsLoading(false);
          }
        } catch {
          // Ignore — initial mount effect will handle the session check.
        }
      } else if (event === 'signedOut') {
        setUser(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Inactivity timer
  useEffect(() => {
    if (!user) return;

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'keydown',
      'mousedown',
      'touchstart',
      'scroll',
    ];

    resetInactivityTimer();
    for (const event of events) {
      window.addEventListener(event, resetInactivityTimer);
    }

    return () => {
      for (const event of events) {
        window.removeEventListener(event, resetInactivityTimer);
      }
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [user, resetInactivityTimer]);

  const handleSignIn = useCallback(async () => {
    // WHY: Navigate to the custom login page rather than triggering Cognito
    // Hosted UI directly. The Login page handles SSO and password flows.
    window.location.href = '/login';
  }, []);

  const handleSignOut = useCallback(async () => {
    await clearSession();
  }, [clearSession]);

  const value: AuthState = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    signIn: handleSignIn,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- useAuth hook is intentionally co-located with the AuthContext/AuthProvider it consumes (module-private context); splitting it out would break encapsulation and churn every import site.
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
